import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Trophy, Medal, ArrowLeft, ChevronDown, Flag } from "lucide-react";
import Avatar from "@/components/Avatar";
import ShareMenu from "@/components/ShareMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANK_CLS = ["text-amber-300", "text-zinc-300", "text-orange-400"];

function Row({ r, open, onToggle }) {
  const top = r.rank <= 3;
  return (
    <div className={`border-b border-zinc-800/80 ${top ? "bg-zinc-950" : ""}`} data-testid={`tour-row-${r.user_id}`}>
      <button onClick={onToggle} className="w-full grid grid-cols-[2.5rem_1fr_auto] sm:grid-cols-[3rem_1fr_6rem_5rem_5rem_6rem_1.5rem] items-center gap-3 px-3 sm:px-4 py-3 text-left hover:bg-zinc-900/60 transition-colors">
        <div className={`font-display font-black text-xl sm:text-2xl ${RANK_CLS[r.rank - 1] || "text-zinc-500"}`} data-testid={`tour-rank-${r.user_id}`}>{r.rank}</div>
        <div className="flex items-center gap-3 min-w-0">
          <Avatar src={r.avatar} name={r.name} size={36} />
          <div className="min-w-0">
            <div className="font-bold text-white truncate" data-testid={`tour-name-${r.user_id}`}>{r.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{[r.division, r.location].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <div className="hidden sm:block text-center"><div className="text-[8px] uppercase tracking-widest text-zinc-500">Events</div><div className="font-mono">{r.events}</div></div>
        <div className="hidden sm:block text-center"><div className="text-[8px] uppercase tracking-widest text-zinc-500">Titles</div><div className="font-mono text-amber-300">{r.titles}</div></div>
        <div className="hidden sm:block text-center"><div className="text-[8px] uppercase tracking-widest text-zinc-500">Podiums</div><div className="font-mono">{r.podiums}</div></div>
        <div className="text-right"><div className="text-[8px] uppercase tracking-widest text-zinc-500">Points</div><div className="font-mono font-black text-2xl text-pb-red leading-none" data-testid={`tour-points-${r.user_id}`}>{r.points}</div></div>
        <ChevronDown className={`hidden sm:block w-4 h-4 text-zinc-600 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-1" data-testid={`tour-results-${r.user_id}`}>
          {r.results.map((x, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-xs border border-zinc-800 bg-black px-3 py-2">
              <div className="min-w-0"><Link to={`/events/${x.tid}`} className="text-white hover:text-pb-red font-semibold truncate block">{x.tournament}</Link><div className="text-[10px] text-zinc-500">{x.date} · {x.label}</div></div>
              <div className="text-right shrink-0"><div className={`font-black ${RANK_CLS[x.rank - 1] || "text-zinc-400"}`}>{x.rank === 1 ? "Champion" : x.rank === 2 ? "Runner-up" : x.rank === 3 ? "3rd" : `${x.rank} of ${x.total}`}</div><div className="font-mono text-pb-red">+{x.points}</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TourStandings() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const season = params.get("season") || "";
  const event = params.get("event") || "";
  useEffect(() => {
    const q = new URLSearchParams(); if (season) q.set("season", season); if (event) q.set("event", event);
    axios.get(`${API}/public/tour/standings?${q}`).then(({ data }) => setData(data)).catch(() => setData({ rows: [], stops: [], seasons: [], events: [] }));
  }, [season, event]);
  const set = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };
  const sel = "bg-zinc-950 border border-zinc-700 text-white text-xs px-3 py-2 focus:outline-none focus:border-pb-red";

  return (
    <div className="min-h-screen bg-black text-white" data-testid="tour-standings-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/tournaments" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white mb-4" data-testid="tour-back"><ArrowLeft className="w-3 h-3" /> Tournament Hub</Link>
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-6">
          <img src="/icons/pbpa-tour.png" alt="PBPA Tour Series" className="w-24 h-24 sm:w-32 sm:h-32 object-contain drop-shadow-[0_0_18px_rgba(163,230,53,0.45)]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-pb-cyan font-bold mb-1">PBPA Tour Series · Season {data?.season || season}</div>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none" data-testid="tour-title">Tour Standings</h1>
            <p className="text-zinc-400 mt-2 text-sm md:text-base max-w-xl">Every Tour Series stop pays season points by finish. Titles break ties. The race resets each calendar year.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="tour-filters">
          <select data-testid="tour-season" value={data?.season || season} onChange={(e) => set("season", e.target.value)} className={sel}>
            {(data?.seasons || []).map((s) => <option key={s} value={s}>Season {s}</option>)}
          </select>
          <select data-testid="tour-event" value={event} onChange={(e) => set("event", e.target.value)} className={sel}>
            <option value="">All events</option>
            {(data?.events || []).map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 ml-auto" data-testid="tour-meta">{data?.stops?.length || 0} stops · {data?.players || 0} players</div>
          <ShareMenu url={`${process.env.REACT_APP_BACKEND_URL}/api/share/tour`} text={`PBPA Tour Series — Season ${data?.season || ""} standings`}
            image="/brand/pbpa-tournaments-share.jpg" testId="share-tour-standings" label="Share" />
        </div>

        <div className="grid lg:grid-cols-[1fr_260px] gap-6">
          <div className="border border-zinc-800 bg-zinc-950/60" data-testid="tour-table">
            {!data ? <div className="p-6 text-zinc-500 text-sm animate-pulse">Loading standings…</div>
              : data.rows.length === 0 ? <div className="p-8 text-center text-zinc-500" data-testid="tour-empty"><Flag className="w-6 h-6 mx-auto mb-2 text-zinc-700" />No completed Tour Series stops yet this season.</div>
              : data.rows.map((r) => <Row key={r.user_id} r={r} open={open === r.user_id} onToggle={() => setOpen(open === r.user_id ? null : r.user_id)} />)}
          </div>
          <div className="space-y-4">
            <div className="border border-zinc-800 bg-zinc-950 p-4" data-testid="tour-points-table">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2"><Medal className="w-3.5 h-3.5" /> Points per stop</div>
              {Object.entries(data?.points_table || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs py-1 border-b border-zinc-900 last:border-0"><span className="text-zinc-400">{k}</span><span className="font-mono text-white">{v}</span></div>
              ))}
              <div className="text-[9px] text-zinc-600 mt-2">Per division entered — play two divisions, earn two results.</div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-4" data-testid="tour-stops">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-pb-cyan font-bold mb-2"><Trophy className="w-3.5 h-3.5" /> Season stops</div>
              {(data?.stops || []).length === 0 && <div className="text-xs text-zinc-600">None yet.</div>}
              {(data?.stops || []).map((s) => (
                <Link key={s.tid} to={`/events/${s.tid}`} className="block py-1.5 border-b border-zinc-900 last:border-0 hover:text-pb-red" data-testid={`tour-stop-${s.tid}`}>
                  <div className="text-xs font-semibold truncate">{s.name}</div>
                  <div className="text-[10px] text-zinc-500">{s.date}{s.location ? ` · ${s.location}` : ""} · {s.divisions} division{s.divisions === 1 ? "" : "s"}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
